import Table, { TableProps } from 'components/Table';
import { GetDiskMonitorResponse } from 'models/response';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import actions from 'state/actions';
import selectors from 'state/selectors';
import styled from 'styled-components/macro';
import utils from 'utils';
import { Wrapper, Title } from '../../styles';

const SubTitle = styled.div`
  font-size: 24px;
  color: ${({ theme }) => theme.colorPalette.grey.grey7};
  margin: 25px 0px;
`;

interface Props {
  hasHistory: boolean;
}

const Einvoice = (props: Props) => {
  const { hasHistory } = props;
  const dispatch = useDispatch();

  const t = utils.intl.useTranslator();
  const { dateFormat } = useSelector(selectors.user.getUserPreference);
  const userPreference = useSelector(selectors.user.getUserPreference);
  const eInvoiceMonitor = useSelector(selectors.normalizer.getEInvoiceMonitor);
  const historyRows = useSelector(selectors.normalizer.getDocumentHistoryEInvoice);
  const wfHistoryRows = useSelector(selectors.normalizer.getMonitorEinvoiceWfHistory);
  const selectedRows = useSelector(selectors.normalizer.getSelectedMonitorRow);
  const pdf = utils.hooks.usePDFImage(selectedRows.length > 0 ? selectedRows[0].protocol : null);
  const columns: TableProps['columns'] = [
    {
      Header: t('company'),
      accessor: 'company',
      filterType: 'free',
    },
    {
      Header: t('documentName'),
      accessor: 'documentName',
      filterType: 'free',
    },
    {
      Header: t('documentType'),
      accessor: 'documentType',
      filterType: 'free',
    },
    {
      accessor: 'acquisitionDate',
      Header: t('acquisitionDate'),
      filterType: 'date',
      filter: utils.date.epochFilterFunction(userPreference.timeZone),
      Cell: ({ value }: { value: number }) => utils.date.convertDate(value, userPreference.timeZone ?? 'UTC'),
      dateFormat: dateFormat,
    },
    {
      Header: t('protocolIn'),
      accessor: 'protocolIn',
      filterType: 'free',
    },
    {
      Header: t('bucketPath'),
      accessor: 'bucketPath',
      filterType: 'free',
    },
    {
      Header: t('position'),
      accessor: 'position',
      filterType: 'free',
    },
  ];

  const historyColumns: TableProps['columns'] = [
    { accessor: 'company', Header: t('company') },
    { accessor: 'documentName', Header: t('documentName') },
    { accessor: 'documentType', Header: t('documentType') },
    { accessor: 'currentUser', Header: t('currentUser') },
    { accessor: 'currentUsername', Header: t('currentUsername') },
    { accessor: 'senderUser', Header: t('senderUser') },
    { accessor: 'senderUsername', Header: t('senderUsername') },
    {
      accessor: 'insDate',
      Header: t('insDate'),
      filterType: 'date',
      filter: utils.date.epochFilterFunction(userPreference.timeZone),
      Cell: ({ value }: { value: number }) => utils.date.convertDate(value, userPreference.timeZone ?? 'UTC'),
      dateFormat: dateFormat,
    },
    { accessor: 'protocolIn', Header: t('protocolIn') },
    { accessor: 'bucketPath', Header: t('bucketPath') },
    { accessor: 'position', Header: t('position') },
    { accessor: 'priority', Header: t('priority') },
    { accessor: 'status', Header: t('status') },
    { accessor: 'incChannel', Header: t('incChannel') },
  ];

  const wfHistoryColumns: TableProps['columns'] = [
    {
      accessor: 'sendDate',
      Header: t('sendDate'),
      filterType: 'date',
      filter: utils.date.epochFilterFunction(userPreference.timeZone),
      Cell: ({ value }: { value: number }) => utils.date.convertDate(value, userPreference.timeZone ?? 'UTC'),
      dateFormat: dateFormat,
    },
    { accessor: 'sender.username', Header: t('username') },
    { accessor: 'type', Header: t('type') },
    { accessor: 'typeDescription', Header: t('typeDescription') },
    { accessor: 'rejectionReason', Header: t('rejectionReason') },
    { accessor: 'note', Header: t('note') },
    { accessor: 'causalCode', Header: t('causalCode') },
  ];

  const onSelection = (selection: GetDiskMonitorResponse[]) => {
    if (!selection.length) {
      dispatch(actions.normalizer.setDocumentHistoryEinvoice([]));
      dispatch(actions.normalizer.setMonitorEInvoiceWfHistory([]));
    }
    dispatch(actions.normalizer.setSelectedMonitorRow(selection));
  };

  return (
    <Wrapper>
      <Title>
        <h2>{t('eInvoice_monitor_title')}</h2>
      </Title>
      {eInvoiceMonitor.length ? (
        <>
          <Table
            id="einvoice-table"
            rowId="protocol"
            hasToolbar
            columns={columns}
            rows={eInvoiceMonitor}
            onSelection={onSelection}
            hasSelection
            hasPagination
            hasResize
            hasSort
            hasFilter
          />
          {pdf}
        </>
      ) : (
        <div>{t('no_data')}</div>
      )}
      {historyRows.length > 0 && hasHistory ? (
        <>
          <SubTitle>{t('document_history')}</SubTitle>
          <Table id="einvoice-history-table" columns={historyColumns} rows={historyRows} hasSort hasFilter hasToolbar />
        </>
      ) : null}
      {wfHistoryRows.length > 0 && hasHistory ? (
        <>
          <SubTitle>{t('workflow_history')}</SubTitle>
          <Table
            id="einvoice-wf-history-table"
            columns={wfHistoryColumns}
            rows={wfHistoryRows}
            hasSort
            hasFilter
            hasToolbar
          />
        </>
      ) : null}
    </Wrapper>
  );
};

export default Einvoice;
